import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, {useEffect, useState} from 'react';
import {LanguagesController, SupportedLanguage} from '../../../../api/translations/languages.controller';
import {TranslationEntry, TranslationsController} from '../../../../api/translations/translations.controller';

type FieldDef = {suffix: string; label: string; multiline?: boolean; isBenefits?: boolean};

const FEATURE_FIELDS: FieldDef[] = [
  {suffix: '', label: 'Tytuł'},
  {suffix: '.description', label: 'Opis', multiline: true},
  {suffix: '.benefits', label: 'Benefity', isBenefits: true},
];

const MODULE_FIELDS: FieldDef[] = [
  {suffix: '', label: 'Tytuł'},
];

type BenefitsEditorProps = {
  value: string;
  onChange: (val: string) => void;
};

const BenefitsEditor = ({value, onChange}: BenefitsEditorProps) => {
  const [draft, setDraft] = useState('');

  const items = value.split('\n').filter(l => l.trim());

  const add = () => {
    const trimmed = draft.trim();
    if (!trimmed) return;
    onChange([...items, trimmed].join('\n'));
    setDraft('');
  };

  const remove = (index: number) => {
    onChange(items.filter((_, i) => i !== index).join('\n'));
  };

  return (
    <Box>
      <TextField
        label="Benefity"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        size="small"
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={add} disabled={!draft.trim()}>
                <AddIcon fontSize="small"/>
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {items.length > 0 && (
        <Box sx={{mt: 0.5}}>
          {items.map((item, i) => (
            <Box key={i} sx={{display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25}}>
              <Typography variant="body2" sx={{flexShrink: 0}}>•</Typography>
              <Typography variant="body2" sx={{flex: 1, minWidth: 0, wordBreak: 'break-word'}}>{item}</Typography>
              <IconButton size="small" onClick={() => remove(i)} sx={{flexShrink: 0}}>
                <DeleteOutlineIcon fontSize="small"/>
              </IconButton>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

type Props = {
  open: boolean
  baseKey: string
  type: 'module' | 'feature'
  onClose: () => void
  onSaved: () => void
}

export const TranslationEditDrawer = ({open, baseKey, type, onClose, onSaved}: Props) => {
  const [languages, setLanguages] = useState<SupportedLanguage[]>([]);
  const [values, setValues] = useState<Record<string, Record<string, string>>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fields = type === 'feature' ? FEATURE_FIELDS : MODULE_FIELDS;

  useEffect(() => {
    if (!open || !baseKey) return;
    setLoading(true);
    Promise.all([
      LanguagesController.getLanguages(),
      TranslationsController.getTranslations(),
    ]).then(([langs, entries]: [SupportedLanguage[], TranslationEntry[]]) => {
      setLanguages(langs);
      const init: Record<string, Record<string, string>> = {};
      langs.forEach(l => {
        init[l.code] = {};
        fields.forEach(f => {
          const entry = entries.find(e => e.key === baseKey + f.suffix && e.language === l.code);
          init[l.code][f.suffix] = entry?.value ?? '';
        });
      });
      setValues(init);
    }).finally(() => setLoading(false));
  }, [open, baseKey]);

  const setValue = (lang: string, suffix: string, val: string) => {
    setValues(prev => ({...prev, [lang]: {...prev[lang], [suffix]: val}}));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const ops: Promise<any>[] = [];
      languages.forEach(l => {
        fields.forEach(f => {
          const val = values[l.code]?.[f.suffix];
          if (val !== undefined) {
            ops.push(TranslationsController.upsertTranslation({
              key: baseKey + f.suffix,
              language: l.code,
              value: val,
            }));
          }
        });
      });
      await Promise.all(ops);
      onSaved();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{sx: {width: 680, top: 64, height: 'calc(100% - 64px)'}}}>
      <Box sx={{p: 3, height: '100%', display: 'flex', flexDirection: 'column'}}>
        <Typography variant="h6" sx={{mb: 0.5}}>Tłumaczenia</Typography>
        <Typography variant="caption" color="text.secondary" sx={{fontFamily: 'monospace', mb: 2, display: 'block'}}>
          {baseKey}
        </Typography>

        {loading ? (
          <Stack alignItems="center" justifyContent="center" flex={1}>
            <CircularProgress size={32}/>
          </Stack>
        ) : (
          <Box sx={{flex: 1, overflowY: 'auto'}}>
            <Stack spacing={3} divider={<Divider/>}>
              {languages.map(lang => (
                <Box key={lang.code}>
                  <Typography variant="subtitle2" sx={{mb: 1.5, color: 'primary.main'}}>
                    {lang.label} ({lang.code})
                  </Typography>
                  <Stack spacing={1.5}>
                    {fields.map(f => (
                      f.isBenefits
                        ? <BenefitsEditor
                            key={f.suffix}
                            value={values[lang.code]?.[f.suffix] ?? ''}
                            onChange={val => setValue(lang.code, f.suffix, val)}
                          />
                        : <TextField
                            key={f.suffix}
                            label={f.label}
                            value={values[lang.code]?.[f.suffix] ?? ''}
                            onChange={e => setValue(lang.code, f.suffix, e.target.value)}
                            fullWidth
                            multiline={f.multiline}
                            minRows={f.multiline ? 3 : 1}
                            size="small"
                          />
                    ))}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.08)'}}>
          <Button onClick={onClose} disabled={saving}>Anuluj</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || loading}>
            {saving ? <CircularProgress size={18}/> : 'Zapisz'}
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};
